import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';

type GradeActionFooterProps = {
  onSaveDraft: () => void;
  onSaveAndNext: () => void;
  onFinalize: () => void;
  onRescan?: () => void;
  onDelete?: () => void;
  disabled?: boolean;
  saveNextDisabled?: boolean;
  className?: string;
  /** Dock to viewport (mobile stacked flow). */
  fixed?: boolean;
};

export function GradeActionFooter({
  onSaveDraft,
  onSaveAndNext,
  onFinalize,
  onRescan,
  onDelete,
  disabled = false,
  saveNextDisabled = false,
  className,
  fixed = false,
}: GradeActionFooterProps): ReactElement {
  const { t } = useTranslation();

  return (
    <footer
      className={cn(
        'z-30 border-t border-slate-200/80 bg-white/95 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-4px_24px_rgba(15,23,42,0.06)] backdrop-blur-md dark:border-slate-700/70 dark:bg-slate-900/95 dark:shadow-[0_-4px_24px_rgba(0,0,0,0.35)]',
        fixed ? 'fixed bottom-0 left-0 right-0' : 'sticky bottom-0',
        className
      )}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            disabled={disabled}
            onClick={onSaveDraft}
            className="h-12 flex-1 rounded-dg-control border border-slate-200/90 bg-white text-dg-button font-semibold text-slate-800 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
          >
            {t('audit.gradeStudio.saveDraft')}
          </button>
          <button
            type="button"
            disabled={disabled || saveNextDisabled}
            onClick={onSaveAndNext}
            className="h-12 flex-[1.15] rounded-dg-control bg-dg-accent-grade text-dg-button font-semibold text-white shadow-sm transition-transform hover:brightness-105 active:scale-[0.99] disabled:opacity-50"
          >
            {t('audit.gradeStudio.saveNext')}
          </button>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            disabled={disabled}
            onClick={onFinalize}
            className="h-11 flex-1 rounded-dg-control border border-emerald-500/35 bg-emerald-50 text-dg-button font-semibold text-emerald-900 hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-700/40 dark:bg-emerald-950/40 dark:text-emerald-100 dark:hover:bg-emerald-950/60"
          >
            {t('audit.gradeStudio.finalize')}
          </button>
          {onRescan ? (
            <button
              type="button"
              disabled={disabled}
              onClick={onRescan}
              className="h-11 flex-1 rounded-dg-control border border-slate-200/90 bg-white text-dg-button font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              {t('audit.row.rescan')}
            </button>
          ) : null}
          {onDelete ? (
            <button
              type="button"
              disabled={disabled}
              onClick={onDelete}
              className="h-11 flex-1 rounded-dg-control border border-red-200/80 bg-red-50 text-dg-button font-semibold text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-950/60"
            >
              {t('audit.row.delete')}
            </button>
          ) : null}
        </div>
      </div>
    </footer>
  );
}
